"use client";

import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { createIncotermLocation } from "~/actions/incotermLocationActions";
import AddressField, { Address } from "~/components/address/AddressField";
import Button from "~/components/buttons/Button";
import { Icon, IconName } from "~/components/icons/Icon";
import Dropdown from "~/components/inputs/Dropdown";
import { TextInput } from "~/components/inputs/TextInput";
import { COUNTRY_CODES } from "~/constants";
import { useCurrentLocale, useI18n } from "~/locales/client";
import { ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import { IncotermLocationOption } from "~/types-and-enums/incotermLocationTypes";
import { getCountryDisplayName } from "~/utils/incotermLocationUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import IncotermLocationOptionContent from "./IncotermLocationOptionContent";

type FormField = "street" | "postal_code" | "city" | "country_code";
export type IncotermLocationFormValues = Record<FormField, string>;

const FIELD_LABEL_KEYS: Record<FormField, string> = {
  street: "incoterm-location.street",
  postal_code: "incoterm-location.postal-code",
  city: "incoterm-location.city",
  country_code: "incoterm-location.country",
};
const FORM_FIELDS = Object.keys(FIELD_LABEL_KEYS) as FormField[];
const EMPTY_VALUES: IncotermLocationFormValues = { street: "", postal_code: "", city: "", country_code: "" };

type IncotermLocationCreateFormProps = {
  /** Prefix for the input ids (unique per IncotermLocationSelect). */
  name: string;
  prefill: Partial<IncotermLocationFormValues>;
  onCancel: () => void;
  onCreated: (location: IncotermLocationOption, duplicateConfirmed: boolean) => void;
  onCreateFailed: (reason: "invalid" | "error") => void;
  onUseExisting: (location: IncotermLocationOption) => void;
};

/**
 * Create-mode content of IncotermLocationSelect: address prefill, street / postal code / city / country form,
 * and the duplicate panel shown when the backend answers 409.
 */
const IncotermLocationCreateForm = ({
  name,
  prefill,
  onCancel,
  onCreated,
  onCreateFailed,
  onUseExisting,
}: IncotermLocationCreateFormProps) => {
  const translate = useI18n();
  const locale = useCurrentLocale();
  const [values, setValues] = useState<IncotermLocationFormValues>({ ...EMPTY_VALUES, ...prefill });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FormField, string>>>({});
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [duplicates, setDuplicates] = useState<IncotermLocationOption[] | null>(null);

  const countryOptions = useMemo(
    () =>
      COUNTRY_CODES.map((code) => ({ value: code, label: `${getCountryDisplayName(code, locale)} (${code})` })).sort(
        (a, b) => a.label.localeCompare(b.label)
      ),
    [locale]
  );

  const requiredError = (field: FormField) =>
    translate("incoterm-location.required", { field: translate(FIELD_LABEL_KEYS[field]) });

  const setValue = (field: FormField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Prefill only the fields the picked address provides. AddressField falls back to the whole suggestion text
  // ("Hamburg, Germany") as street when the place has no route, so a street containing a comma is ignored.
  const handleAddressPicked = (address: Address) => {
    const picked: Partial<IncotermLocationFormValues> = {
      ...(address.street && !address.street.includes(",") ? { street: address.street } : {}),
      ...(address.postal_code ? { postal_code: address.postal_code } : {}),
      ...(address.city ? { city: address.city } : {}),
      ...(address.country_code ? { country_code: address.country_code.toUpperCase() } : {}),
    };
    setValues((prev) => ({ ...prev, ...picked }));
    setFieldErrors((prev) => ({ ...prev, ...Object.fromEntries(Object.keys(picked).map((key) => [key, undefined])) }));
  };

  const validate = (trimmed: IncotermLocationFormValues) => {
    const errors: Partial<Record<FormField, string>> = {};
    if (!trimmed.postal_code) errors.postal_code = requiredError("postal_code");
    if (!trimmed.city) errors.city = requiredError("city");
    if (!COUNTRY_CODES.includes(trimmed.country_code)) errors.country_code = requiredError("country_code");
    return errors;
  };

  const submit = async (confirmDuplicate: boolean) => {
    const trimmed: IncotermLocationFormValues = {
      street: values.street.trim(),
      postal_code: values.postal_code.trim(),
      city: values.city.trim(),
      country_code: values.country_code.trim().toUpperCase(),
    };
    const errors = validate(trimmed);
    setFieldErrors(errors);
    setBannerError(null);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      const result = await createIncotermLocation({
        ...(trimmed.street ? { street: trimmed.street } : {}),
        postal_code: trimmed.postal_code,
        city: trimmed.city,
        country_code: trimmed.country_code,
        ...(confirmDuplicate ? { confirm_duplicate: true } : {}),
      });
      if (result.status === "created") {
        onCreated(result.location, confirmDuplicate);
      } else if (result.status === "duplicate") {
        setDuplicates(result.matches);
      } else if (result.status === "invalid") {
        const mapped = FORM_FIELDS.filter((field) => result.errors[field]?.length);
        setFieldErrors(
          Object.fromEntries(
            mapped.map((field) => [
              field,
              result.errors[field].includes("required") ? requiredError(field) : result.errors[field].join(", "),
            ])
          )
        );
        if (mapped.length === 0) setBannerError(result.message || translate("incoterm-location.create-error"));
        setDuplicates(null);
        onCreateFailed("invalid");
      } else {
        setBannerError(translate("incoterm-location.create-error"));
        onCreateFailed("error");
      }
    } catch (error) {
      console.error("Error creating incoterm location:", error);
      setBannerError(translate("incoterm-location.create-error"));
      onCreateFailed("error");
    } finally {
      setIsSaving(false);
    }
  };

  const errorText = (message?: string) =>
    message ? <span className="text-[8px] md:text-[10px] font-medium text-red-600">{message}</span> : null;

  const textField = (field: Exclude<FormField, "country_code">, required: boolean) => (
    <div className="flex flex-col gap-1 min-w-0">
      <label htmlFor={`${name}-${field}`} className="text-xs font-medium text-gray-500">
        {translate(FIELD_LABEL_KEYS[field])}
        {required && " *"}
      </label>
      <TextInput
        name={`${name}-${field}`}
        value={values[field]}
        onChange={(e) => setValue(field, e.target.value)}
        classNames={twMerge("w-full", fieldErrors[field] && "border-red-500")}
        autoComplete="off"
      />
      {errorText(fieldErrors[field])}
    </div>
  );

  if (duplicates) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">{translate("incoterm-location.duplicate-title")}</span>
          <button
            type="button"
            onClick={() => setDuplicates(null)}
            className="text-xs font-medium text-gray-600 hover:text-gray-900"
          >
            {translate("incoterm-location.back")}
          </button>
        </div>
        {duplicates.map((match) => (
          <div key={match.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <div className="flex-1 min-w-0">
              <IncotermLocationOptionContent option={match} />
            </div>
            <Button
              buttonStyle={ButtonTypesEnum.primary}
              text={translate("incoterm-location.use-existing")}
              onClick={() => onUseExisting(match)}
              buttonClassName="w-fit px-3 py-1.5"
            />
          </div>
        ))}
        <div className="flex justify-end px-4 py-3">
          <Button
            buttonStyle={ButtonTypesEnum.default}
            text={translate("incoterm-location.create-anyway")}
            onClick={() => submit(true)}
            disabled={isSaving}
            isLoading={isSaving}
            buttonClassName="w-fit px-3 py-1.5 border border-gray-300"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900">{translate("incoterm-location.create-title")}</span>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
        >
          <Icon name={IconName.ArrowLeft} size={spacing[3]} color={colors.gray[600]} />
          {translate("incoterm-location.back")}
        </button>
      </div>
      <AddressField
        onChange={handleAddressPicked}
        placeholder={translate("incoterm-location.prefill-hint")}
        useRelativePositioning
      />
      {textField("street", false)}
      <div className="grid grid-cols-2 gap-3">
        {textField("postal_code", true)}
        {textField("city", true)}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-gray-500">{translate("incoterm-location.country")} *</span>
        <Dropdown
          name={`${name}-country_code`}
          options={countryOptions}
          value={values.country_code}
          onChange={(value) => setValue("country_code", value)}
          placeholder={translate("incoterm-location.country")}
          error={!!fieldErrors.country_code}
          useRelativePositioning
          openUpward
        />
        {errorText(fieldErrors.country_code)}
      </div>
      {bannerError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <span className="text-sm text-red-700">{bannerError}</span>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          buttonStyle={ButtonTypesEnum.default}
          text={translate("incoterm-location.cancel")}
          onClick={onCancel}
          disabled={isSaving}
          buttonClassName="w-fit px-4 py-2 border border-gray-300"
        />
        <Button
          buttonStyle={ButtonTypesEnum.primary}
          text={translate("incoterm-location.save")}
          onClick={() => submit(false)}
          disabled={isSaving}
          isLoading={isSaving}
          buttonClassName="w-fit px-4 py-2"
        />
      </div>
    </div>
  );
};

export default IncotermLocationCreateForm;
